/* BillingSuccess — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/billing/success`. Lands here from Stripe Checkout
 * (success_url=…/billing/success?session_id=…). Polls
 * GET /api/payments/checkout-session/{session_id}/status.
 *
 * Iteration_12 HONESTY RULE — never render "Subscription Active" unless
 * the response includes:
 *    - stripe_subscription_id != null
 *    - subscription_active == true
 *    - provisioned_subscription.status ∈ {"active","trialing"}
 *
 * On a slow provisioning path (>30 polls = ~60s) we surface an "activation
 * delayed" message with the session_id — never a false confirm.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  Clock,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AuthShell from "./AuthShell";
import { V2Button } from "@/components/v2/FormPrimitives";

const MAX_POLLS = 30;
const POLL_INTERVAL_MS = 2000;

async function trackEvent(eventName, props) {
  try {
    await api.post("/events", { event_name: eventName, properties: props || {} });
  } catch {
    /* swallow — analytics is best effort */
  }
}

function isActive(data) {
  if (!data) return false;
  if (!data.subscription_active) return false;
  if (!data.stripe_subscription_id) return false;
  const prov = data.provisioned_subscription;
  if (!prov || !prov.stripe_subscription_id) return false;
  const status = String(prov.status || "").toLowerCase();
  return status === "active" || status === "trialing";
}

function isTerminalFailure(data) {
  const payment = String((data && data.payment_status) || "").toLowerCase();
  const sessionStatus = String((data && data.status) || "").toLowerCase();
  return payment === "failed" || sessionStatus === "expired";
}

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", info: null, attempt: 0 });

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;

    if (!sessionId) {
      (async () => {
        try {
          const { data } = await api.get("/payments/my-subscription");
          if (cancelled) return;
          const sub = (data && data.subscription) || null;
          const active = !!sub
            && !!sub.stripe_subscription_id
            && (sub.status === "active" || sub.status === "trialing")
            && !sub.is_stub;
          if (active) {
            const info = {
              plan_code: (data && data.plan && data.plan.code) || sub.plan_code,
              billing_cycle: sub.billing_cycle,
              stripe_subscription_id: sub.stripe_subscription_id,
              amount_total: null,
              currency: (data && data.plan && data.plan.currency) || "usd",
            };
            setState({ phase: "success", info, attempt: 0 });
            trackEvent("Subscription Purchased (recovery)", {
              stripe_subscription_id: sub.stripe_subscription_id,
              plan_code: info.plan_code,
              billing_cycle: sub.billing_cycle,
              recovery_reason: "missing_session_id",
            });
            return;
          }
          setState({
            phase: "orphan_recovery",
            info: { recovered_at: new Date().toISOString() },
            attempt: 0,
          });
        } catch {
          if (cancelled) return;
          setState({
            phase: "orphan_recovery",
            info: { recovered_at: new Date().toISOString() },
            attempt: 0,
          });
        }
      })();
      return () => { cancelled = true; };
    }

    async function poll() {
      attempt += 1;
      try {
        const { data } = await api.get(`/payments/checkout-session/${sessionId}/status`);
        if (cancelled) return;

        if (isActive(data)) {
          setState({ phase: "success", info: data, attempt });
          trackEvent("Subscription Purchased", {
            session_id: sessionId,
            plan_id: data.plan_id,
            plan_code: data.plan_code,
            billing_cycle: data.billing_cycle,
            amount_total: data.amount_total,
            stripe_subscription_id: data.stripe_subscription_id,
          });
          return;
        }
        if (isTerminalFailure(data)) {
          setState({ phase: "failed", info: data, attempt });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ phase: "activation_delayed", info: data, attempt });
          return;
        }
        setState({ phase: "polling", info: data, attempt });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attempt >= MAX_POLLS) {
          setState({ phase: "error", info: { message: formatApiError(e) }, attempt });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const titleMap = {
    polling:
      state.info && String(state.info.payment_status || "").toLowerCase() === "paid"
        ? "Payment received — activating your subscription…"
        : "Processing your subscription…",
    success: "Subscription active.",
    failed: "Payment didn't go through.",
    error: "Payment didn't go through.",
    orphan_recovery: "We couldn't confirm your payment right now.",
    activation_delayed: "Payment received — activation delayed.",
  };

  return (
    <AuthShell testId="billing-success-page" title={titleMap[state.phase] || "Subscription"}>
      <div className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 text-center">
        {state.phase === "polling" && (
          <div data-testid="billing-success-polling">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-v2-accent" />
            <p className="text-v2-body-md text-v2-textMuted">
              Hold tight while we confirm the payment. Attempt {state.attempt} of {MAX_POLLS}.
            </p>
          </div>
        )}

        {state.phase === "success" && (
          <div data-testid="billing-success-confirmed">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-v2-success" />
            <p
              data-testid="billing-success-plan-code"
              className="text-v2-body-md text-v2-textMuted mb-2"
            >
              You're on <span className="text-v2-text">{state.info?.plan_code || "your plan"}</span>{" "}
              ({state.info?.billing_cycle}). Welcome aboard.
            </p>
            {state.info?.amount_total ? (
              <p
                data-testid="billing-success-amount"
                className="text-v2-body-sm text-v2-textSubtle mb-6"
              >
                Charged ${(Number(state.info.amount_total) / 100).toFixed(2)}{" "}
                {(state.info.currency || "usd").toUpperCase()}
              </p>
            ) : null}
            <div className="flex flex-col gap-3 mt-4">
              <V2Button as={Link} to="/dashboard" data-testid="billing-success-dashboard" fullWidth>
                Go to dashboard <ArrowRight className="w-3.5 h-3.5" />
              </V2Button>
              <Link
                data-testid="billing-success-billing"
                to="/account/billing"
                className="text-v2-body-sm text-v2-textMuted hover:text-v2-text transition-colors"
              >
                Manage subscription
              </Link>
            </div>
          </div>
        )}

        {(state.phase === "failed" || state.phase === "error") && (
          <div data-testid="billing-success-failed">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-v2-danger" />
            <p className="text-v2-body-md text-v2-textMuted mb-6">
              {state.info?.message || "Stripe reported the payment as failed or expired."}
            </p>
            <V2Button as={Link} to="/pricing" data-testid="billing-success-retry" fullWidth>
              Back to pricing <ArrowRight className="w-3.5 h-3.5" />
            </V2Button>
          </div>
        )}

        {state.phase === "orphan_recovery" && (
          <div data-testid="billing-success-orphan-recovery">
            <Clock className="w-12 h-12 mx-auto mb-4 text-v2-warning" />
            <p className="text-v2-body-md text-v2-textMuted mb-2">
              Please check <span className="text-v2-text">/account/billing</span> in a moment.
              Stripe sometimes drops the confirmation query param on redirect — your subscription
              may still have activated.
            </p>
            <p
              data-testid="billing-success-orphan-recovery-timestamp"
              className="text-v2-body-sm text-v2-textSubtle mb-6 break-all"
            >
              If you don't see your subscription there, contact support with this timestamp:{" "}
              <span className="text-v2-text">{state.info?.recovered_at}</span>
            </p>
            <div className="flex flex-col gap-3">
              <V2Button
                as={Link}
                to="/account/billing"
                data-testid="billing-success-orphan-recovery-billing"
                fullWidth
              >
                Open billing center <ArrowRight className="w-3.5 h-3.5" />
              </V2Button>
              <Link
                data-testid="billing-success-orphan-recovery-pricing"
                to="/pricing"
                className="text-v2-body-sm text-v2-textMuted hover:text-v2-text transition-colors"
              >
                Back to pricing
              </Link>
            </div>
          </div>
        )}

        {state.phase === "activation_delayed" && (
          <div data-testid="billing-success-activation-delayed">
            <Clock className="w-12 h-12 mx-auto mb-4 text-v2-warning" />
            <p className="text-v2-body-md text-v2-textMuted mb-2">
              Your card was charged, but we haven't finished activating the subscription yet.
              This usually resolves on its own within a few minutes.
            </p>
            <p
              data-testid="billing-success-session-id"
              className="text-v2-body-sm text-v2-textSubtle mb-6 break-all"
            >
              Session: <span className="text-v2-text">{sessionId}</span>
            </p>
            <div className="flex flex-col gap-3">
              <V2Button
                as={Link}
                to="/account/billing"
                data-testid="billing-success-go-billing"
                fullWidth
              >
                Open billing center <ArrowRight className="w-3.5 h-3.5" />
              </V2Button>
              <p className="text-v2-body-sm text-v2-textSubtle">
                Still stuck? Refresh in a couple of minutes or contact support with the session id above.
              </p>
            </div>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
